import { ExclamationTriangleIcon, PencilSquareIcon, PlusIcon } from '@heroicons/react/24/outline';
import type { OrderMethodDetail } from '../../../services/vendors';
import { DrawerSection } from '../Drawer';
import { StatusBadge, type Tone } from '../StatusBadge';
import { methodBlockLabel, methodBlockReason } from './vendorDispatch';

const KIND_TONES: Record<string, Tone> = {
    email: { bg: 'bg-blue-600' },
    external: { bg: 'bg-teal-600' }
};

const KIND_LABELS: Record<string, string> = { email: 'Email', external: 'Vendor portal' };

function MethodRow({
    method,
    canEdit,
    onEdit
}: {
    method: OrderMethodDetail;
    canEdit: boolean;
    onEdit: () => void;
}) {
    const block = methodBlockReason(method);
    // `isUsable` is the server's verdict; `block` only supplies the wording.
    const unusable = !method.isUsable;

    return (
        <div
            className={`flex gap-3 px-4 py-3 ${
                unusable ? 'bg-red-50/40' : ''
            } ${method.isActive ? '' : 'opacity-60'}`}
        >
            <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                <span className="text-sm font-medium text-gray-800 truncate">{method.name}</span>

                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] text-gray-400">{method.id}</span>
                    {method.kind === 'email' && (
                        <span
                            className="text-[11px] text-gray-500 truncate"
                            title={method.poRecipients.join(', ')}
                        >
                            {method.poRecipients.length === 0
                                ? 'no recipients'
                                : `${method.poRecipients.length} recipient${
                                      method.poRecipients.length === 1 ? '' : 's'
                                  }`}
                        </span>
                    )}
                    <span className="text-[11px] text-gray-400">
                        {method.references.linkedProducts} products ·{' '}
                        {method.references.livePurchaseOrders} live POs
                    </span>
                </div>

                {unusable && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-700">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" />
                        {block ? methodBlockLabel(block) : 'Not usable'}: cannot be ordered through
                    </span>
                )}
            </div>

            <div className="flex flex-col items-end gap-2 shrink-0">
                <StatusBadge
                    status={method.kind}
                    toneOverride={KIND_TONES}
                    label={KIND_LABELS[method.kind] ?? method.kind}
                />
                {canEdit && (
                    <button
                        type="button"
                        onClick={onEdit}
                        aria-label={`Edit ${method.name}`}
                        className="text-gray-400 hover:text-gray-700 transition-colors"
                    >
                        <PencilSquareIcon className="h-4 w-4" />
                    </button>
                )}
            </div>
        </div>
    );
}

interface OrderMethodsSectionProps {
    methods: OrderMethodDetail[];
    canEdit: boolean;
    onAdd: () => void;
    onEdit: (method: OrderMethodDetail) => void;
}

export function OrderMethodsSection({ methods, canEdit, onAdd, onEdit }: OrderMethodsSectionProps) {
    return (
        <DrawerSection
            title={`Ordering methods${methods.length ? ` (${methods.length})` : ''}`}
            action={
                canEdit ? (
                    <button
                        type="button"
                        onClick={onAdd}
                        className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <PlusIcon className="h-3.5 w-3.5" />
                        Add method
                    </button>
                ) : undefined
            }
        >
            {methods.length === 0 ? (
                <p className="text-sm text-rose-600 font-medium">
                    No ordering methods: this vendor cannot be ordered from, and its products cannot
                    be assigned one.
                </p>
            ) : (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {methods.map((method) => (
                        <MethodRow
                            key={method.id}
                            method={method}
                            canEdit={canEdit}
                            onEdit={() => onEdit(method)}
                        />
                    ))}
                </div>
            )}
        </DrawerSection>
    );
}
